import { z } from "zod";

/**
 * The daily canary (reliability 1.4, #826; `docs/requirements/slo-canary.md` R4, ADR 0014
 * decisions 2 and 3). A canary tenant (`tenants.purpose = 'canary'`) runs one schedule a day
 * through the normal path; the runner's verdict loop (`runner/canary/`) judges each run from
 * skyData's own records only (the run's status, the load step's statement results, the extract's
 * counts and the transform's counts), never from the warehouse.
 */

export const CANARY_OUTCOMES = ["green", "failed", "missed"] as const;
export type CanaryOutcome = (typeof CANARY_OUTCOMES)[number];

/** The verdict's closed codes (R4.6): the first failing check wins. */
export const CANARY_CODES = [
  "GREEN",
  "RUN_FAILED",
  "RUN_PARTIALLY_SUCCEEDED",
  "RUN_CANCELLED",
  "ROWS_MISMATCH",
  "DBT_FAILED",
  "TOO_SLOW",
  "RUN_UNFINISHED",
  "NO_RUN",
] as const;
export type CanaryCode = (typeof CANARY_CODES)[number];

/** Which check a `checks` entry is about. */
export const CANARY_CHECKS = ["run_status", "rows", "dbt", "duration", "window"] as const;

const count = z.number().int().min(0);

/**
 * One failed check (`canary_results.checks`): closed values, ids and numbers. A rows check names
 * the connection by id and the table by the entity name skyData stores (`entity_progress`), never
 * a warehouse table name; a run or dbt check carries the cause's closed source and code.
 */
export const canaryCheckSchema = z
  .object({
    check: z.enum(CANARY_CHECKS),
    dataflowId: z.uuid().optional(),
    entityName: z.string().min(1).max(512).optional(),
    expected: count.optional(),
    /** The load's COPY result; null when the driver reported none. */
    rowsLoaded: count.nullable().optional(),
    /** The load's own post-load count. */
    totalRecords: count.nullable().optional(),
    /** The extract's count (`entity_progress.records`). */
    records: count.nullable().optional(),
    /** The load's per-table status. */
    status: z.string().max(24).optional(),
    causeSource: z.string().max(16).optional(),
    causeCode: z.string().max(40).optional(),
    testsFailed: count.nullable().optional(),
    modelsFailed: count.nullable().optional(),
    durationSeconds: count.optional(),
    maxDurationSeconds: count.optional(),
  })
  .strict();
export type CanaryCheck = z.infer<typeof canaryCheckSchema>;

/** `{ "<dataflowId>": { "<entityName>": <rows> } }`: the exact count each table lands with. */
export const canaryExpectedRowsSchema = z
  .record(
    z.uuid(),
    z
      .record(z.string().min(1).max(512), z.number().int().min(0).max(1_000_000_000))
      .refine((tables) => Object.keys(tables).length > 0, "Name at least one table."),
  )
  .refine((flows) => Object.keys(flows).length > 0, "Name at least one connection.");
export type CanaryExpectedRows = z.infer<typeof canaryExpectedRowsSchema>;

/** `PUT /platform/canary/:tenantId` (R4.8 step 8). */
export const putCanaryBodySchema = z
  .object({
    instanceId: z.uuid(),
    scheduleId: z.uuid(),
    expectedRows: canaryExpectedRowsSchema,
    /** From the firing to the run's end; past it the verdict is `TOO_SLOW`. */
    maxDurationSeconds: z.number().int().min(60).max(86_400),
    /** How long after the newest judged firing a day with no run counts as missed. */
    windowHours: z.number().int().min(25).max(72).optional(),
  })
  .strict();
export type PutCanaryBody = z.infer<typeof putCanaryBodySchema>;

/** How many results `GET /platform/canary` lists per canary tenant. */
export const CANARY_RESULTS_SHOWN = 14;

export interface CanaryResultView {
  day: string;
  outcome: CanaryOutcome;
  code: CanaryCode;
  runId: string | null;
  scheduledFor: string | null;
  durationMs: number | null;
  checks: CanaryCheck[];
  at: string;
}

export interface CanaryTenantView {
  tenantId: string;
  tenantDisplayName: string;
  instanceId: string;
  scheduleId: string;
  expectedRows: CanaryExpectedRows;
  maxDurationSeconds: number;
  windowHours: number;
  lastWindowDay: string | null;
  /** Where judging starts: the newest change of the instance, schedule or expected rows. */
  judgedFrom: string;
  createdAt: string;
  updatedAt: string;
  /** Newest first, at most `CANARY_RESULTS_SHOWN`. */
  results: CanaryResultView[];
}

export interface CanaryListView {
  canaries: CanaryTenantView[];
}

/** The telemetry event of each verdict (R1). */
export const CANARY_RESULT_EVENT = "skydata.canary.result";

/**
 * `canary:<tenantId>:<day>:<runId|none>:<generation>:<what>`: one outbox event per verdict. The run
 * and its retry generation are in the key, so a second failure or recovery on the same day (a rerun
 * by hand, a retry) is sent too; a run judged `RUN_UNFINISHED` has its own word, so the outcome it
 * ends with later is still sent. A retried pass of the loop sends nothing twice.
 */
export function canaryDedupeKey(
  tenantId: string,
  day: string,
  runId: string | null,
  generation: number,
  what: "failed" | "missed" | "recovered" | "unfinished",
): string {
  return `canary:${tenantId}:${day}:${runId ?? "none"}:${generation}:${what}`;
}
